import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useApiClient } from '../../apiClient';
import { classifyRead } from '../../features/_shared/readState';

export interface UserSearchResult {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

interface UserSearchApiResponse {
  items: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatarUrl?: string;
  }>;
  hasMore: boolean;
  after?: string;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

export function useEntitySearch(organizationId: string | undefined, rawQuery: string) {
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const client = useApiClient();

  useEffect(() => {
    if (rawQuery.length < MIN_QUERY_LENGTH) {
      setDebouncedQuery('');
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(rawQuery), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [rawQuery]);

  const query = useQuery({
    queryKey: ['user-search', organizationId, debouncedQuery],
    queryFn: async () => {
      const response = await client.fetchApi<UserSearchApiResponse, {}>({
        endpoint: `users/search/${organizationId}?q=${encodeURIComponent(debouncedQuery)}&take=10`,
        method: 'GET',
      });
      return (response?.items ?? []).map(user => ({
        id: user.id,
        name: [user.firstName, user.lastName].filter(Boolean).join(' '),
        email: user.email,
        avatarUrl: user.avatarUrl,
      }));
    },
    enabled: Boolean(organizationId) && debouncedQuery.length >= MIN_QUERY_LENGTH,
    staleTime: 30_000,
  });

  return {
    results: query.data ?? [],
    isLoading: query.isFetching,
    isError: query.isError,
    hasQuery: debouncedQuery.length >= MIN_QUERY_LENGTH,
    /**
     * Which of the four read states this is (run 101).
     *
     * The three fields above cannot express `never-issued`, and they collapse
     * it into the WORST available answer: with no organization selected the
     * query is disabled, so `isFetching` is false, `isError` is false and
     * `query.data` is undefined — `results` is therefore `[]`, and a caller
     * that reads it renders "No users found for 'ab'" about a search it never
     * ran.
     *
     * That is not reachable today, and the reason it is not is worth writing
     * down because it is fragile: the ONLY entry into user-search mode is a
     * palette item carrying `disabled={!selectedOrganization}`, guarded twice
     * (the item's `disabled` prop, and `onSelect={disabled ? undefined : …}`).
     * So the hook's correctness is currently a property of a sibling
     * component's props. Exposing the state here moves it back into the hook,
     * where the next caller inherits it instead of having to re-derive the
     * guard — the same reason D62 stopped asking callers to restate a gate.
     */
    state: classifyRead(query.isError, query.isInitialLoading, query.data),
  };
}
